import { useEffect, useState, type FormEvent } from "react";
import type { ResellerProfile } from "../types";

export function BrandingPage({
  profile,
  onSave,
  onRefresh
}: {
  profile: ResellerProfile | null;
  onSave: (data: {
    productName: string;
    logoUrl: string;
    supportUrl: string;
    nameservers: string;
    company: string;
  }) => Promise<void>;
  onRefresh: () => Promise<void>;
}) {
  const [productName, setProductName] = useState("Hosting Panel");
  const [logoUrl, setLogoUrl] = useState("");
  const [supportUrl, setSupportUrl] = useState("");
  const [nameservers, setNameservers] = useState("");
  const [company, setCompany] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setProductName(profile.productName || "Hosting Panel");
    setLogoUrl(profile.logoUrl || "");
    setSupportUrl(profile.supportUrl || "");
    setNameservers((profile.nameservers || []).join("\n"));
    setCompany(profile.company || "");
  }, [profile]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await onSave({
        productName,
        logoUrl,
        supportUrl,
        nameservers,
        company
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const loginHint = profile?.username
    ? `/?reseller=${encodeURIComponent(profile.username)}`
    : "/?reseller=USERNAME";

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Reseller Branding</h1>
          <p>
            White-label product name, logo, nameservers, and support URL for
            your customers. Share the branded login link below.
          </p>
        </div>
        <div className="topbar-actions">
          <button type="button" className="btn btn-ghost" onClick={() => onRefresh()}>
            Refresh
          </button>
        </div>
      </div>

      {!profile ? (
        <div className="error-banner">Reseller profile not loaded.</div>
      ) : (
        <form className="panel-form" onSubmit={submit}>
          <div className="panel-form-title">Brand settings</div>
          {error ? <div className="error-banner">{error}</div> : null}
          {saved ? (
            <div className="chip" style={{ marginBottom: 12 }}>
              Branding saved
            </div>
          ) : null}

          <div className="form-grid">
            <div className="field">
              <label>Product name</label>
              <input
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                required
                placeholder="Acme Panel"
              />
            </div>
            <div className="field">
              <label>Company</label>
              <input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Acme Hosting LLC"
              />
            </div>
            <div className="field">
              <label>Logo URL</label>
              <input
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://…"
              />
            </div>
            <div className="field">
              <label>Support URL</label>
              <input
                value={supportUrl}
                onChange={(e) => setSupportUrl(e.target.value)}
                placeholder="https://support.example.com"
              />
            </div>
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Nameservers (one per line)</label>
              <textarea
                value={nameservers}
                onChange={(e) => setNameservers(e.target.value)}
                rows={3}
                placeholder={"ns1.example.com\nns2.example.com"}
              />
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-label">Branded login</div>
            <p className="card-meta">
              Customers open{" "}
              <code className="mono">{loginHint}</code> to see your product name
              and logo on the login screen.
            </p>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo preview"
                style={{ maxHeight: 48, marginTop: 8, objectFit: "contain" }}
              />
            ) : null}
          </div>

          <button className="btn btn-primary btn-inline" disabled={busy} type="submit">
            {busy ? "Saving…" : "Save branding"}
          </button>
        </form>
      )}
    </>
  );
}
